import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setDone(true);
      toast.success('Password reset successfully');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((e) => e.msg).join(', ')
          : 'Could not reset password. Please request a new link.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Missing token guard
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800">InterviewWala</span>
          </Link>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center" data-testid="reset-password-no-token">
            <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 mb-2">Invalid reset link</h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              This reset link is missing a token. Please request a fresh password reset email.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white">Request new link</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-800">InterviewWala</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          {!done ? (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-teal-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Choose a new password</h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Pick a password you haven't used before. Minimum 6 characters.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-700 font-medium">New password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      autoFocus
                      className="h-12 bg-white border-slate-200 focus:border-teal-500 focus:ring-teal-500 pr-12"
                      data-testid="reset-password-new-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 hover:bg-slate-100"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="reset-password-toggle-visibility"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 bg-white border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="reset-password-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold shadow-lg shadow-teal-500/25 transition-all duration-200"
                  disabled={loading}
                  data-testid="reset-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>Reset password</>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Password reset</h1>
              <p className="text-slate-500 text-sm leading-relaxed mb-6">
                Your password has been updated. Redirecting you to login…
              </p>
              <Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto" />
            </div>
          )}

          {!done && (
            <div className="mt-6 pt-6 border-t border-slate-200 text-center">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-teal-600 font-medium transition-colors"
                data-testid="reset-password-back-to-login"
              >
                Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
